import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import HeroVideo from "@/components/HeroVideo";
import { ArrowRight, Sprout, Hammer, Truck, ShieldCheck } from "lucide-react";

const BOTTLES_IMG =
  "https://customer-assets.emergentagent.com/job_7837e2b0-0c58-44b6-ba36-d34f0cc5b7e9/artifacts/s0t3veiu_Final%20Design%20-%20spice%20bottles.png";
const CHILLI_BLACK_IMG =
  "https://customer-assets.emergentagent.com/job_blend-luxe-2/artifacts/18p1kmpj_Red%20chilly.png";
const SMART_CAP_IMG =
  "https://customer-assets.emergentagent.com/job_7837e2b0-0c58-44b6-ba36-d34f0cc5b7e9/artifacts/3w4niem0_Smart%20Measuring%20Cap.png";

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts()
      .then((all) => setProducts(all.filter((p) => p.category !== "refill")))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 opacity-[0.18]">
          <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-[#D4AF37] blur-[160px]" />
          <div className="absolute -bottom-40 -right-20 w-[36rem] h-[36rem] rounded-full bg-[#8A2323] blur-[180px]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <p className="overline mb-6">Fresh from Farm to Kitchen · Est. 2020</p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-[#F5F5F0]">
                Spices, the way <br />
                they were <em className="text-[#D4AF37] not-italic font-light">meant</em>
                <br />
                to reach you.
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[#F5F5F0]/75 leading-relaxed font-light">
                Single-origin spices from India&rsquo;s finest growing regions — sun-dried,
                cold-milled and sealed into jars within seven days of harvest. No
                preservatives. No fillers. No middlemen.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start">
                <Link
                  to="/shop"
                  data-testid="hero-shop-cta"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] text-xs uppercase tracking-[0.3em] hover:bg-[#e6c258] transition-all hover:-translate-y-0.5"
                >
                  Shop Fresh Spices
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-story-cta"
                  className="inline-flex items-center px-8 py-4 border border-white/15 text-[#F5F5F0] text-xs uppercase tracking-[0.3em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  Our Farm Story
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                {[
                  { v: "100%", k: "Pure" },
                  { v: "0", k: "Preservatives" },
                  { v: "7 days", k: "Farm → Jar" },
                ].map((s) => (
                  <div key={s.k}>
                    <p className="font-heading text-2xl md:text-3xl text-[#D4AF37]">{s.v}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A85] mt-1">
                      {s.k}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] max-w-md mx-auto min-h-[520px]">
                <HeroVideo />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 right-10 hidden md:block z-10 text-right">
          <p className="overline text-[#D4AF37]/80">No. 01</p>
          <p className="font-heading text-xs text-[#F5F5F0]/60 tracking-wider mt-1">The Collection</p>
        </div>
      </section>

      {/* FROM-FARM-TO-KITCHEN JOURNEY */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5">
              <p className="overline mb-4">The Journey</p>
              <h2 className="font-heading text-4xl md:text-5xl text-[#F5F5F0] tracking-tight leading-[1.05]">
                Four steps. <span className="italic text-[#D4AF37]">Zero shortcuts.</span>
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-6">
              <p className="text-[#F5F5F0]/70 font-light leading-relaxed text-lg">
                Most spice on the shelf today has been sitting in warehouses for months.
                We do it differently. From the moment of harvest to the moment of sealing,
                every step is timed, hand-supervised and finished in under a week.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { n: "01", icon: Sprout, t: "Hand-Harvested", d: "From twelve trusted family farms across India. We know the soil, the grower, and the season." },
              { n: "02", icon: ShieldCheck, t: "Sun-Dried Pure", d: "Open-air, slow-dried for up to fourteen days. No chemical treatment, no irradiation." },
              { n: "03", icon: Hammer, t: "Cold-Milled Fresh", d: "Stone-milled in small batches to preserve aroma, colour and the volatile oils that matter." },
              { n: "04", icon: Truck, t: "Sealed Within 7 Days", d: "Packed into BPA-free jars with our Smart Measuring Cap. Shipped before the aroma fades." },
            ].map((s) => (
              <div key={s.n} className="border border-white/8 bg-[#0e0e0e] p-7 hover:border-[#D4AF37]/40 transition-colors duration-500">
                <div className="flex items-center justify-between mb-6">
                  <s.icon size={22} strokeWidth={1.3} className="text-[#D4AF37]" />
                  <span className="overline">{s.n}</span>
                </div>
                <h3 className="font-heading text-2xl text-[#F5F5F0] mb-3 leading-tight">{s.t}</h3>
                <p className="text-sm text-[#8A8A85] font-light leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE COLLECTION */}
      <section className="py-24 md:py-32" data-testid="home-collection">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="overline mb-4">The Collection</p>
              <h2 className="font-heading text-4xl md:text-6xl text-[#F5F5F0] tracking-tight leading-none">
                Four blends. <br />
                <span className="text-[#D4AF37] italic font-light">All single-origin.</span>
              </h2>
            </div>
            <Link
              to="/shop"
              className="hidden md:inline-flex items-center gap-2 gold-link text-xs uppercase tracking-[0.25em]"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* SOURCING USP — every spice from where it grows best */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5 relative overflow-hidden">
        <div className="absolute -inset-x-40 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16 items-end">
            <div className="md:col-span-7">
              <p className="overline mb-4">Our Sourcing</p>
              <h2 className="font-heading text-4xl md:text-6xl text-[#F5F5F0] tracking-tight leading-[1.02]">
                Every spice, from <br />
                <span className="italic text-[#D4AF37]">where it grows best.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#F5F5F0]/70 font-light leading-relaxed text-lg">
                Climate, soil and weather conspire differently across India.
                We trace each spice back to the one region where its terroir is
                unmistakable &mdash; and source nowhere else.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { spice: "Red Chilli", place: "Guntur", state: "Andhra Pradesh", note: "World-famous Guntur chillies — colour, fragrance, refined heat.", color: "#9C1B1B" },
              { spice: "Turmeric", place: "Golden Fields", state: "Maharashtra", note: "Curcumin-rich rhizomes from Maharashtra&rsquo;s golden belts.", color: "#D4AF37" },
              { spice: "Coriander", place: "Lush Fields", state: "Rajasthan", note: "Citrus-floral coriander from Rajasthan&rsquo;s lush green fields.", color: "#6E7B3C" },
              { spice: "Garam Masala", place: "Spice Coast", state: "Kerala", note: "Whole spices from Kerala&rsquo;s storied Spice Coast.", color: "#8A5A2C" },
            ].map((s) => (
              <div key={s.spice} className="relative bg-[#0c0c0c] border border-white/8 p-7 hover:border-[#D4AF37]/40 transition-colors duration-500">
                <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundColor: s.color, opacity: 0.7 }} />
                <p className="overline mb-5">{s.state}</p>
                <h3 className="font-heading text-2xl text-[#F5F5F0] leading-tight mb-1">{s.spice}</h3>
                <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-5">{s.place} · {s.state}</p>
                <p className="text-sm text-[#8A8A85] font-light leading-relaxed" dangerouslySetInnerHTML={{ __html: s.note }} />
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-xs uppercase tracking-[0.3em] text-[#8A8A85]">
            Climate &nbsp;·&nbsp; Soil &nbsp;·&nbsp; Weather &nbsp;·&nbsp; Tradition
          </p>
        </div>
      </section>

      {/* SMART MEASURING CAP */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[28rem] h-[28rem] rounded-full bg-[#D4AF37]/8 blur-[140px]" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p className="overline mb-6">Designed In-House</p>
              <h2 className="font-heading text-4xl md:text-6xl text-[#F5F5F0] tracking-tight leading-[1.02] mb-8">
                The Smart <br />
                <span className="italic text-[#D4AF37]">Measuring Cap.</span>
              </h2>
              <p className="text-lg text-[#F5F5F0]/75 leading-relaxed font-light mb-10">
                Every jar comes with our signature dual-action cap — pour, sprinkle, or
                spoon, whichever your dish demands. No mess, no spillage, no plastic
                spoons. Total control in your hands.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-5 pb-6 border-b border-white/5">
                  <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] font-heading text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-[#F5F5F0] mb-1">Shake &amp; Pour</h3>
                    <p className="text-sm text-[#8A8A85] font-light">
                      Three to four micro-holes for an even, controlled sprinkle — perfect
                      for finishing a dish like salt.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5 pb-6 border-b border-white/5">
                  <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] font-heading text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-[#F5F5F0] mb-1">Spoon &amp; Measure</h3>
                    <p className="text-sm text-[#8A8A85] font-light">
                      A wider opening on the other side lets you scoop the precise amount
                      your recipe asks for.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] font-heading text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-[#F5F5F0] mb-1">Hygienic &amp; Sealed</h3>
                    <p className="text-sm text-[#8A8A85] font-light">
                      BPA-free, moisture-locking, and engineered to keep your spices
                      cupboard-fresh — week after week.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/15 to-transparent blur-3xl" />
                <img
                  src={SMART_CAP_IMG}
                  alt="Smart Measuring Cap"
                  loading="lazy"
                  decoding="async"
                  className="relative w-full h-auto object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FRESHNESS BAND */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="relative aspect-[4/5] bg-[#0e0e0e] overflow-hidden">
              <img
                src={CHILLI_BLACK_IMG}
                alt="Red Chilli Powder bottle"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/40 via-transparent to-transparent" />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="overline mb-6">Why Freshness Matters</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#F5F5F0] tracking-tight leading-[1.05] mb-8">
              Aroma is the <em className="italic text-[#D4AF37] not-italic">first ingredient</em>
              <br /> to disappear.
            </h2>
            <p className="text-lg text-[#F5F5F0]/75 font-light leading-relaxed mb-6">
              A spice ground today is at its best for a few weeks — no more. Once the
              volatile oils evaporate, what&rsquo;s left is colour without character. That&rsquo;s
              why we refuse to grind in advance. Every jar we ship was ground in the
              same week.
            </p>
            <ul className="space-y-3 text-sm text-[#F5F5F0]/80 font-light">
              {[
                "Cold-milled within hours of grinding",
                "Sealed in BPA-free jars with Smart Cap",
                "Shipped within 7 days of harvest batch",
                "No preservatives. No fillers. No adulteration.",
              ].map((s) => (
                <li key={s} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="overline mb-6">Begin the Shift</p>
          <h2 className="font-heading text-4xl md:text-6xl text-[#F5F5F0] leading-[1.05] tracking-tight">
            One jar can change <br />
            <span className="italic text-[#D4AF37]">every meal you cook.</span>
          </h2>
          <Link
            to="/shop"
            data-testid="footer-cta-shop"
            className="inline-flex items-center gap-3 mt-12 px-10 py-4 bg-[#D4AF37] text-[#0A0A0A] text-xs uppercase tracking-[0.3em] hover:bg-[#e6c258] transition-all hover:-translate-y-0.5"
          >
            Shop the Collection <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
